export const translations = {
    en: {
        header: { help: 'Help', about: 'About Us', events: 'Events', loginSignup: 'Login / Sign Up', feedback: 'Feedback' },
        hero: {
            title: 'Take Control of Your Health with UME',
            subtitle: 'A platform that listens to you, keeps your records in order, and helps you stay on top of your care. Guided by Welby, your AI health companion.',
            joinWaitlist: 'Join the Waitlist',
            seeHowWorks: 'See How It Works'
        },
        popup: {
            title: 'Get Early Access to UME Health',
            subtitle: 'Be among the first to experience Welby, your AI health companion. Join our waitlist for exclusive updates and early access.',
            welbyNote: '💬 Welby can respond to any health-related questions you have - from understanding medical terms to scheduling appointments. Ask away!',
            email: 'Email Address',
            phone: 'Phone Number (Optional)',
            message: 'Message (Optional)',
            joinWaitlist: 'Join Waitlist',
            skip: 'Maybe Later',
            success: 'Thank you! We\'ll be in touch soon.',
            placeholder: {
                email: 'your@email.com',
                phone: '(555) 123-4567',
                message: 'Tell us about your healthcare needs...'
            }
        },
        problem: {
            heading: 'What UME Solves',
            subheading: 'Healthcare shouldn\'t feel confusing or overwhelming. Here\'s how we help.',
            portals: { title: 'Too many portals', subtitle: 'All-in-One Access', desc: 'See all your lab results and appointments in one place—no more juggling multiple apps.', welby: 'Welby organizes everything in one place!'},
            instructions: { title: 'Confusing instructions', subtitle: 'Clear Guidance', desc: 'Get personalized reminders and AI guidance that helps you make sense of your health data.', welby: 'Welby turns confusion into clarity!'},
            appointments: { title: 'Missed appointments', subtitle: 'Smart Reminders', desc: 'Stay on top of your health with reminders that actually help you follow through.', welby: 'Welby never lets you forget!'},
            caregiver: { title: 'Caregiver stress', subtitle: 'Shared Access', desc: 'Secure shared access for loved ones, so you never have to navigate care alone.', welby: 'Welby shares the load with loved ones!'}
        },
        solution: {
            welbyTitle: 'Meet Welby!',
            welbyIntro: 'Welby is your always-available guide inside the UME app, offering personalized answers, reminders, and support to ensure you\'re never navigating care alone.',
            tagline: 'U have a friend in ME. UME Health!',
            features: [
                'Personal AI guidance to help you make sense of your health data',
                'All your labs and appointments in one easy-to-access place',
                'Secure login with HIPAA-compliant verification for peace of mind',
                'Export your medical history anytime for a smooth transition to new providers',
                'Stay on top of your appointments with easy-to-access notes',
                'Voice commands with Siri or Google for hands-free health management'
            ],
            tasks: 'Your Tasks',
            pickupMed: 'Pick up medication',
            scheduleFU: 'Schedule follow-up',
            dailyReminders: 'Daily reminders'
        },
        howItWorks: {
            heading: 'How it works',
            step1: { title: 'Upload your information', desc: 'Take a photo or enter details' },
            step2: { title: 'Welby simplifies it', desc: 'Turns it into clear steps' },
            step3: { title: 'You stay on track', desc: 'Reminders + guidance' }
        },
        demo: {
            heading: 'See Welby in Action',
            subheading: 'Experience how Welby turns complex healthcare moments into clear, actionable guidance.',
            assistant: 'Welby Assistant',
            welcome: "Hi, I'm Welby — your UME Health companion. Ask me about healthcare, health insurance, appointments, prescriptions, recovery steps, or follow-up planning. I provide friendly informational support, not medical advice.",
            presenceTitle: 'Talk directly with Welby',
            presenceBody: 'Ask a question and Welby responds like a calm companion who can help you think through the next step.',
            statusReady: 'Here with you',
            statusThinking: 'Thinking it through',
            memoryTitle: 'Welby remembers this conversation',
            memoryEmpty: 'Ask about appointments, recovery, or insurance and Welby will keep the thread going.',
            transcriptTitle: 'Conversation trail',
            inputLabel: 'Tell Welby what you need help with.',
            inputPlaceholder: 'I need to reschedule my follow-up for next week.',
            askButton: 'Talk to Welby',
            thinkingButton: 'Welby is thinking...',
            youLabel: 'You',
            welbyLabel: 'Welby',
            voiceInput: 'Voice input',
            voiceStop: 'Stop listening',
            voiceUnavailable: 'Voice input is not supported in this browser.',
            voicePermissionError: 'Microphone access was blocked. Please allow microphone access and try again.',
            voiceInputError: 'Welby could not hear that clearly. Please try again or type your message.',
            botVoiceOn: 'Bot voice on',
            botVoiceOff: 'Bot voice off',
            botVoiceUnavailable: 'AWS Tiffany voice is not available right now.',
            botVoiceEnglishOnly: 'AWS Tiffany voice is available for English replies only.',
            botVoiceStyle: 'AWS Tiffany voice',
            uploadButton: 'Attach files',
            uploadHint: 'Add up to 3 PDFs, images, or text files. Files are reviewed only in this demo chat and are not stored here.',
            selectedFilesLabel: 'Selected files',
            removeFile: 'Remove file',
            fileReviewDefault: 'Please review the attached medical documents.',
            fileTooLarge: 'Each file must be 10 MB or smaller.',
            fileUnsupported: 'Please attach a PDF, image, text, CSV, JSON, XML, or Markdown file.',
            fileLimit: 'You can attach up to 3 files at a time.',
            fileReadError: 'Welby could not read one of those files. Please try a different file.'
        },
        trust: {
            heading: 'Why UME Health?',
            para1: 'At UME Health, we believe managing your health should feel simple and stress-free. Our platform organizes your medical records, lab results, and appointments all in one place. With personalized guidance from our AI assistant and tools designed to keep you on track, you can make confident decisions about your care.',
            para2: 'We believe everyone deserves accessible, transparent, and human-centered care. We are committed to helping you feel supported, informed, and in control so you can focus on living your life with peace of mind.',
            quote: 'I watched too many patients leave with instructions they didn\'t understand and couldn\'t get support on once they got home.',
            quoteAuthor: '— Dr. Evangelina Snow',
            missionTitle: 'Our mission is simple: to make healthcare easier to understand, easier to follow, and easier to manage — for everyone.',
            missionSubtitle: 'Designed for real patients navigating real care.'
        },
        faq: {
            heading: 'Frequently Asked Questions',
            subheading: 'Everything you need to know about UME Health',
            items: [
                { q: 'How is my data protected?', a: 'Your health data is protected with industry-leading security measures including HIPAA-compliant encryption, secure authentication, and strict access controls. We never share your data without your explicit consent.' },
                { q: 'How quickly can caregivers access data?', a: 'Caregivers with authorized access can view shared health information instantly through their secure account. You control exactly what information is shared and can revoke access at any time.' },
                { q: 'Is Welby available in other languages?', a: 'Yes! Welby supports multiple languages to ensure everyone can access clear health guidance in their preferred language. We\'re continuously expanding our language support based on community needs.' },
                { q: 'What happens to my existing medical records?', a: 'You can easily import your existing medical records, lab results, and appointment history into UME Health. We support multiple formats and make the transition seamless.' },
                { q: 'How much does UME Health cost?', a: 'We offer both free and premium tiers. Early waitlist members will receive 3 free months of Premium at launch. Detailed pricing will be shared as we approach our official release.' }
            ]
        },
        cta: {
            heading: 'Ready to Take Control?',
            subheading: 'Be among the first to experience Welby',
            incentive: 'Sign up today for 3 free months of Premium at launch'
        },
    },
    es: {
        header: { help: 'Ayuda', about: 'Nosotros', events: 'Eventos', feedback: 'Comentario', loginSignup: 'Registrarse / Iniciar Sesión' },
        hero: {
            title: 'Toma el control de tu salud con UME Health',
            subtitle: 'Una plataforma que te escucha, mantiene tus registros organizados y te ayuda a estar al día con tu cuidado. Guiado por Welby, tu asistente de salud con IA.',
            joinWaitlist: 'Únete a la lista de espera',
            seeHowWorks: 'Ver cómo funciona'
        },
        popup: {
            title: 'Obtén acceso temprano a UME Health',
            subtitle: 'Sé uno de los primeros en experimentar Welby, tu compañero de salud con IA. Únete a nuestra lista de espera para actualizaciones exclusivas y acceso temprano.',
            welbyNote: '💬 Welby puede responder a cualquier pregunta relacionada con la salud que tengas - desde comprender términos médicos hasta programar citas. ¡Pregunta sin miedo!',
            email: 'Dirección de correo electrónico',
            phone: 'Número de teléfono (Opcional)',
            message: 'Mensaje (Opcional)',
            joinWaitlist: 'Unirse a la lista de espera',
            skip: 'Quizás después',
            success: '¡Gracias! Nos pondremos en contacto pronto.',
            placeholder: {
                email: 'tu@email.com',
                phone: '(555) 123-4567',
                message: 'Cuéntanos sobre tus necesidades de atención médica...'
            }
        },
        problem: {
            heading: 'Lo que UME resuelve',
            subheading: 'La atención médica no debería ser confusa. Aquí te mostramos cómo ayudamos.',
            portals: { title: 'Demasiados portales', subtitle: 'Acceso todo en uno', desc: 'Ve todos tus resultados de laboratorio y citas en un solo lugar.', welby: '¡Welby organiza todo en un solo lugar!'},
            instructions: { title: 'Instrucciones confusas', subtitle: 'Orientación clara', desc: 'Recibe recordatorios personalizados y orientación de IA que te ayude a comprender tus datos de salud.', welby: '¡Welby convierte la confusión en claridad!'},
            appointments: { title: 'Citas perdidas', subtitle: 'Recordatorios inteligentes', desc: 'Mantente al día con tu salud con recordatorios que te ayuden a seguir adelante.', welby: '¡Welby nunca te deja olvidar!'},
            caregiver: { title: 'Estrés del cuidador', subtitle: 'Acceso compartido', desc: 'Acceso compartido seguro para seres queridos.', welby: '¡Welby comparte la carga con tus seres queridos!'}
        },
        solution: {
            welbyTitle: '¡Hola Welby!',
            welbyIntro: 'Welby es tu guía siempre disponible dentro de la aplicación UME.',
            tagline: 'Tienes un amigo en MI. ¡UME Health!',
            features: [
                'Orientación personal de IA',
                'Todos tus labs y citas en un solo lugar',
                'Inicio de sesión seguro cumplidor de HIPAA',
                'Exporta tu historial médico en cualquier momento',
                'Mantente atento a tus citas con notas rápidas',
                'Comandos de voz con Siri o Google'
            ],
            tasks: 'Tus tareas',
            pickupMed: 'Recoge medicamento',
            scheduleFU: 'Programar seguimiento',
            dailyReminders: 'Recordatorios diarios'
        },
        howItWorks: {
            heading: 'Cómo funciona',
            step1: { title: 'Sube tu información', desc: 'Toma una foto o ingresa detalles' },
            step2: { title: 'Welby lo simplifica', desc: 'Lo convierte en pasos claros' },
            step3: { title: 'Te mantienes en el camino', desc: 'Recordatorios + orientación' }
        },
        demo: {
            heading: 'Mira a Welby en acción',
            subheading: 'Experimenta cómo Welby convierte momentos complejos en orientación clara.',
            assistant: 'Asistente Welby',
            welcome: 'Hola, soy Welby, tu compañero de UME Health. Puedes preguntarme sobre salud, seguro médico, citas, medicamentos, recuperación o planificación de seguimiento. Brindo apoyo informativo y amigable, no consejo médico.',
            presenceTitle: 'Habla directamente con Welby',
            presenceBody: 'Haz una pregunta y Welby responde como un compañero tranquilo que te ayuda a pensar en el siguiente paso.',
            statusReady: 'Aquí contigo',
            statusThinking: 'Pensándolo contigo',
            memoryTitle: 'Welby recuerda esta conversación',
            memoryEmpty: 'Pregunta sobre citas, recuperación o seguro y Welby mantendrá el contexto.',
            transcriptTitle: 'Historial de conversación',
            inputLabel: 'Dile a Welby con qué necesitas ayuda.',
            inputPlaceholder: 'Necesito cambiar mi cita de seguimiento para la próxima semana.',
            askButton: 'Hablar con Welby',
            thinkingButton: 'Welby está pensando...',
            youLabel: 'Tú',
            welbyLabel: 'Welby',
            voiceInput: 'Entrada de voz',
            voiceStop: 'Dejar de escuchar',
            voiceUnavailable: 'La entrada de voz no está disponible en este navegador.',
            voicePermissionError: 'El acceso al micrófono fue bloqueado. Permite el acceso e inténtalo de nuevo.',
            voiceInputError: 'Welby no pudo escuchar con claridad. Inténtalo de nuevo o escribe tu mensaje.',
            botVoiceOn: 'Voz de Welby activa',
            botVoiceOff: 'Voz de Welby apagada',
            botVoiceUnavailable: 'La voz AWS Tiffany no está disponible ahora.',
            botVoiceEnglishOnly: 'La voz AWS Tiffany solo está disponible para respuestas en inglés.',
            botVoiceStyle: 'Voz AWS Tiffany',
            uploadButton: 'Adjuntar archivos',
            uploadHint: 'Agrega hasta 3 PDFs, imágenes o archivos de texto. Los archivos se revisan solo en este chat demo y no se almacenan aquí.',
            selectedFilesLabel: 'Archivos seleccionados',
            removeFile: 'Quitar archivo',
            fileReviewDefault: 'Por favor revisa los documentos médicos adjuntos.',
            fileTooLarge: 'Cada archivo debe ser de 10 MB o menos.',
            fileUnsupported: 'Adjunta un PDF, imagen, texto, CSV, JSON, XML o Markdown.',
            fileLimit: 'Puedes adjuntar hasta 3 archivos a la vez.',
            fileReadError: 'Welby no pudo leer uno de esos archivos. Intenta con otro archivo.'
        },
        trust: {
            heading: '¿Por qué UME Health?',
            para1: 'En UME Health, creemos que administrar tu salud debe ser simple y sin estrés.',
            para2: 'Creemos que todos merecen atención accesible, transparente y centrada en el ser humano.',
            quote: 'Vi demasiados pacientes irse con instrucciones que no entendían.',
            quoteAuthor: '— Dra. Evangelina Snow',
            storyTitle: 'La experiencia de Karina',
            storyP1: 'Welby fue inspirado por algo profundamente personal.',
            storyP2: 'Lo que debería haber sido manejable se volvió estresante rápidamente.',
            storyP3: 'Al mismo tiempo, Karina había pasado más de una década trabajando en el sistema de salud.',
            storyP4: 'Welby fue construido en la intersección de esas dos experiencias.',
            storyP5: 'Porque nadie debería tener que descifrar todo solo.',
            missionTitle: 'Nuestra misión es simple: hacer que la atención médica sea más fácil de entender, seguir y manejar.',
            missionSubtitle: 'Diseñado para pacientes reales navegando cuidados reales.'
        },
        faq: {
            heading: 'Preguntas frecuentes',
            subheading: 'Todo lo que necesitas saber sobre UME Health',
            items: [
                { q: '¿Cómo están protegidos mis datos?', a: 'Tus datos de salud están protegidos con medidas de seguridad líderes en la industria.' },
                { q: '¿Qué tan rápido pueden acceder los cuidadores?', a: 'Los cuidadores autorizados pueden ver la información de salud compartida al instante.' },
                { q: '¿Welby está disponible en otros idiomas?', a: '¡Sí! Welby admite múltiples idiomas.' },
                { q: '¿Qué sucede con mis registros médicos existentes?', a: 'Puedes importar fácilmente tus registros médicos existentes.' },
                { q: '¿Cuánto cuesta UME Health?', a: 'Ofrecemos niveles gratuitos y premium.' }
            ]
        },
        cta: {
            heading: '¿Listo para tomar el control?',
            subheading: 'Sé uno de los primeros en experimentar Welby',
            incentive: 'Regístrate hoy y obtén 3 meses gratis de Premium al lanzamiento'
        },
    },
    tl: {
        header: { help: 'Tulong', about: 'Tungkol sa Amin', events: 'Events', feedback: 'Feedback', loginSignup: 'Login / Sign Up' },
        hero: {
            title: 'Kontrolin ang Iyong Kalusugan gamit ang UME Health',
            subtitle: 'Isang platform na nakikinig sa iyo, nagsasaayos ng iyong mga record, at tumutulong sa iyong pag-aalaga. Pinapatakbo ng Welby, ang iyong AI health companion.',
            joinWaitlist: 'Sumali sa Waitlist',
            seeHowWorks: 'Tingnan kung Paano Ito Gumagana'
        },
        popup: {
            title: 'Makakuha ng Early Access sa UME Health',
            subtitle: 'Maging isa sa mga unang makakaranas ng Welby, ang iyong AI health companion. Sumali sa aming waitlist para sa mga eksklusibong update at early access.',
            welbyNote: '💬 Maaaring tumugon si Welby sa anumang health-related na tanong na mayroon ka - mula sa pag-unawa ng medical terms hanggang sa pag-schedule ng appointments. Magtanong ka!',
            email: 'Email Address',
            phone: 'Numero ng Telepono (Opsyonal)',
            message: 'Mensahe (Opsyonal)',
            joinWaitlist: 'Sumali sa Waitlist',
            skip: 'Baka Mamaya',
            success: 'Salamat! Makakausap ka namin soon.',
            placeholder: {
                email: 'your@email.com',
                phone: '(555) 123-4567',
                message: 'Sabihin sa amin ang tungkol sa iyong mga pangangailangan sa healthcare...'
            }
        },
        problem: {
            heading: 'Ang Nalulutas ng UME',
            subheading: 'Ang healthcare ay hindi dapat mahirap. Narito kung paano kami tumutulong.',
            portals: { title: 'Masyadong maraming portal', subtitle: 'All-in-One Access', desc: 'Tingnan ang lahat ng iyong lab results at appointments sa isang lugar.', welby: 'Inaayos ni Welby ang lahat sa isang lugar!'},
            instructions: { title: 'Nakakaligtaang mga tagubilin', subtitle: 'Malinaw na Gabay', desc: 'Makatanggap ng personalized na paalala at gabay ng AI.', welby: 'Ginagawang malinaw ni Welby ang kalituhan!'},
            appointments: { title: 'Napalampasang appointment', subtitle: 'Smart Reminders', desc: 'Manatiling updated sa iyong kalusugan.', welby: 'Hindi ka pababayaan ni Welby na malimutan!'},
            caregiver: { title: 'Stress ng caregiver', subtitle: 'Shared Access', desc: 'Secure na shared access para sa mga mahal sa buhay.', welby: 'Ibinabahagi ni Welby ang pasanin sa iyong mga mahal sa buhay!'}
        },
        solution: {
            welbyTitle: 'Makilala ang Welby!',
            welbyIntro: 'Si Welby ay ang iyong laging available na gabay sa loob ng UME app.',
            tagline: 'May kaibigan ka sa AKIN. UME Health!',
            features: [
                'Personal na gabay ng AI',
                'Lahat ng iyong labs at appointments sa isang lugar',
                'Secure login na sumusunod sa HIPAA',
                'I-export ang iyong medical history anumang oras',
                'Manatiling updated sa iyong appointments',
                'Voice commands sa Siri o Google'
            ],
            tasks: 'Ang Iyong mga Gawain',
            pickupMed: 'Kunin ang gamot',
            scheduleFU: 'I-schedule ang follow-up',
            dailyReminders: 'Pang-araw-araw na paalala'
        },
        howItWorks: {
            heading: 'Paano Ito Gumagana',
            step1: { title: 'I-upload ang iyong impormasyon', desc: 'Kumuha ng larawan o maglagay ng mga detalye' },
            step2: { title: 'Pinapasimple ito ng Welby', desc: 'Ginagawang malinaw na mga hakbang' },
            step3: { title: 'Manatili sa doaan', desc: 'Mga paalala + gabay' }
        },
        demo: {
            heading: 'Makita ang Welby sa Aksyon',
            subheading: 'Maranasan kung paano nag-convert ng Welby ng komplikadong sandali sa malinaw na payo.',
            assistant: 'Welby Assistant',
            welcome: 'Hi, ako si Welby — ang iyong UME Health companion. Maaari mo akong tanungin tungkol sa healthcare, health insurance, appointments, prescriptions, recovery steps, o follow-up planning. Magiliw akong nagbibigay ng impormasyong suporta, hindi medikal na payo.',
            presenceTitle: 'Direktang makipag-usap kay Welby',
            presenceBody: 'Magtanong at sasagot si Welby na parang isang kalmadong kasama na tumutulong sa susunod mong hakbang.',
            statusReady: 'Kasama mo ako',
            statusThinking: 'Pinag-iisipan ko ito',
            memoryTitle: 'Naaalala ni Welby ang usapang ito',
            memoryEmpty: 'Magtanong tungkol sa appointments, recovery, o insurance at itutuloy ni Welby ang usapan.',
            transcriptTitle: 'Takbo ng usapan',
            inputLabel: 'Sabihin kay Welby kung ano ang kailangan mong tulong.',
            inputPlaceholder: 'Kailangan kong ilipat ang follow-up ko sa susunod na linggo.',
            askButton: 'Kausapin si Welby',
            thinkingButton: 'Nag-iisip si Welby...',
            youLabel: 'Ikaw',
            welbyLabel: 'Welby',
            voiceInput: 'Voice input',
            voiceStop: 'Itigil ang pakikinig',
            voiceUnavailable: 'Hindi supported ang voice input sa browser na ito.',
            voicePermissionError: 'Na-block ang microphone access. Payagan ang microphone at subukan ulit.',
            voiceInputError: 'Hindi malinaw ang narinig ni Welby. Subukan ulit o i-type ang mensahe.',
            botVoiceOn: 'Boses ni Welby on',
            botVoiceOff: 'Boses ni Welby off',
            botVoiceUnavailable: 'Hindi available ngayon ang AWS Tiffany voice.',
            botVoiceEnglishOnly: 'Available lang ang AWS Tiffany voice para sa English replies.',
            botVoiceStyle: 'AWS Tiffany voice',
            uploadButton: 'Mag-attach ng files',
            uploadHint: 'Magdagdag ng hanggang 3 PDF, image, o text file. Nirereview lang ang files sa demo chat na ito at hindi ini-store dito.',
            selectedFilesLabel: 'Napiling files',
            removeFile: 'Tanggalin ang file',
            fileReviewDefault: 'Pakireview ang naka-attach na medical documents.',
            fileTooLarge: 'Bawat file ay dapat 10 MB o mas maliit.',
            fileUnsupported: 'Mag-attach ng PDF, image, text, CSV, JSON, XML, o Markdown file.',
            fileLimit: 'Hanggang 3 files lang ang puwedeng i-attach kada beses.',
            fileReadError: 'Hindi nabasa ni Welby ang isa sa mga file. Subukan ang ibang file.'
        },
        trust: {
            heading: 'Bakit UME Health?',
            para1: 'Sa UME Health, naniniwala kami na ang pag-aalaga sa kalusugan ay dapat simple at walang stress.',
            para2: 'Naniniwala kami na ang lahat ay karapat-dapat sa accessible, transparent, at human-centered na pag-aalaga.',
            quote: 'Nakita ko ang masyadong maraming pasyente na umalis ng walang maintindihang mga tagubilin.',
            quoteAuthor: '— Dr. Evangelina Snow',
            storyTitle: 'Ang Karanasan ng Karina',
            storyP1: 'Ang Welby ay inspirado ng isang personal na karanasan.',
            storyP2: 'Ang dapat na maging simple ay mabilis na naging komplikado.',
            storyP3: 'Sa parehong oras, si Karina ay gumugol ng mahigit sampung taon sa sistema ng healthcare.',
            storyP4: 'Ang Welby ay binuo sa intersection ng dalawang karanasang ito.',
            storyP5: 'Dahil walang dapat mag-isa sa pag-unawa ng lahat.',
            missionTitle: 'Ang aming misyon ay simple: gawing mas madali ang healthcare na maunawaan, sundin, at pamahalaan.',
            missionSubtitle: 'Dinisenyo para sa tunay na mga pasyente na naglalakbay ng tunay na pag-aalaga.'
        },
        faq: {
            heading: 'Mga Madalas na Itinatanong',
            subheading: 'Lahat ng kailangan mong malaman tungkol sa UME Health',
            items: [
                { q: 'Paano protektado ang aking data?', a: 'Ang iyong health data ay protektado ng industry-leading na security measures.' },
                { q: 'Gaano kabilis makaka-access ang mga caregiver?', a: 'Ang mga authorized na caregiver ay maaaring makita ang shared health information kaagad.' },
                { q: 'Available ba ang Welby sa ibang wika?', a: 'Oo! Ang Welby ay sumusuporta sa maraming wika.' },
                { q: 'Ano ang mangyayari sa aking existing medical records?', a: 'Madali mong ma-import ang iyong existing medical records.' },
                { q: 'Magkano ang UME Health?', a: 'Nag-aalok kami ng libre at premium tiers.' }
            ]
        },
        cta: {
            heading: 'Handa ka nang Magkontrol?',
            subheading: 'Maging isa sa mga unang makakaranas kay Welby',
            incentive: 'Mag-sign up ngayon para sa 3 libreng buwan ng Premium sa launch'
        },
    }
};

export const languageNames = {
    en: 'English',
    es: 'Spanish',
    tl: 'Tagalog'
};

export const speechLocales = {
    en: 'en-US',
    es: 'es-US',
    tl: 'tl-PH'
};